import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import i18n from './i18n';
import AlertMessage from "./components/AlertMessage/AlertMessage";
import ErrorScreen from "./components/ErrorScreen/ErrorScreen";
import { ERROR_TYPES } from "./components/ErrorScreen/errorCodes";
import { useClarity } from "./hooks/useClarity";
import useTheme from "./hooks/useTheme";

const TransactionStatus = React.lazy(() => import("./components/index"));

function App({ axiosInstance, token }) {
  const [, setLoader] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ message: "", type: "", icon: "" });
  const [tenant, setTenant] = useState("default");
  const [tenantReady, setTenantReady] = useState(false);
  const [errorType, setErrorType] = useState(null);
  const clarityId = process.env.REACT_APP_CLARITY_ID;
  useClarity(clarityId);
  useTheme();

  const setAlertMessageFn = useCallback((e) => {
    setAlertMessage(e);
    const timer = setTimeout(() => setAlertMessage({ message: "", type: "" }), 8000);
    return () => clearTimeout(timer); 
  }, []);

  const detectTenant = () => {
    // Use tenant pre-detected in index.html inline script if available
    if (window.__DETECTED_TENANT__) return window.__DETECTED_TENANT__;

    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    const knownTenants = (process.env.REACT_APP_KNOWN_TENANTS || '').split(',');
    const normalizedSubdomain = subdomain.replace(/[-_.]/g, '').toLowerCase();
    const matchedTenant = knownTenants.find(tenant =>
      normalizedSubdomain.includes(tenant.toLowerCase())
    );
    return matchedTenant || 'default';
  };

  const validateTenantAssets = async (tenant) => {
    try {
      const response = await fetch(`./assets/${tenant}/style.css`, { method: 'HEAD' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return true;
    } catch (error) {
      console.warn(`Error validando estilo para el tenant "${tenant}":`, error);
      return false;
    }
  };

  const loadTenantStyles = (tenant) => {
    return new Promise((resolve) => {
      const linkId = "tenant-style";
      const existingLink = document.getElementById(linkId);
      const href = `./assets/${tenant}/style.css`;

      // Safety timeout: resolve after 3s even if onload never fires
      const timeout = setTimeout(resolve, 3000);
      const done = () => { clearTimeout(timeout); resolve(); };

      if (existingLink) {
        existingLink.onload = done;
        existingLink.onerror = done;
        existingLink.href = href;
      } else {
        const link = document.createElement("link");
        link.id = linkId;
        link.rel = "stylesheet";
        link.onload = done;
        link.onerror = done;
        link.href = href;
        document.head.appendChild(link);
      }
    });
  };

  useEffect(() => {
    const init = async () => {
      const detectedTenant = detectTenant();
      setTenant(detectedTenant);

      // If the inline script in index.html already loaded the correct tenant CSS,
      // skip validation and loading entirely
      const existingLink = document.getElementById("tenant-style");
      if (existingLink && existingLink.getAttribute("href")?.includes(detectedTenant)) {
        setTenantReady(true);
        return;
      }

      const tenantAssetsValid = await validateTenantAssets(detectedTenant);
      if (tenantAssetsValid) {
        await loadTenantStyles(detectedTenant);
      } else {
        await loadTenantStyles(detectedTenant);
        setErrorType(ERROR_TYPES.TENANT_LOAD_FAILED);
      }
      setTenantReady(true);
    };
    init();
  }, []);

  return (
    <div className="App">
      {errorType ? (
        <I18nextProvider i18n={i18n}>
          <ErrorScreen errorType={errorType} tenant={tenant} />
        </I18nextProvider>
      ) : tenantReady && i18n.isInitialized ? (
        <I18nextProvider i18n={i18n}>
          <Router>
            <Routes>
              <Route
                exact
                path="/"
                element={
                  <TransactionStatus
                    axiosInstance={axiosInstance}
                    setLoader={setLoader}
                    setAlertMessage={setAlertMessageFn}
                    setErrorType={setErrorType}
                    token={token}
                    resolvedTenant={tenant}
                    i18n={i18n}
                  />
                }
              />
            </Routes>
          </Router>
        </I18nextProvider>
      ) : (
        <div style={{ width: '100%', height: '100vh', backgroundColor: 'var(--bg-body, #ffffff)' }} />
      )}
      <AlertMessage alertMessage={alertMessage} />
    </div>
  );
}

export default App;
